// src/components/Navbars/NavbarMenu.js
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MKTypography from "components/MKTypography";
import PropTypes from "prop-types";

/**
 * NavbarMenu
 * 负责渲染某一个顶层菜单（比如“联系我们”、“下载客户端”）下面的下拉项
 *
 * props:
 * - anchorEl: 当前触发下拉的 DOM 元素（用于定位）
 * - open: 是否展开
 * - onClose: 关闭回调
 * - routes: 顶层 routes 数组（和 Navbar 用的是同一个）
 * - dropdownName: 当前被 hover / 点击的顶层菜单名字
 * - openWechatModal: 点击“微信客服 / WeChat”时触发的函数
 */
export default function NavbarMenu({
  anchorEl,
  open,
  onClose,
  routes,
  dropdownName,
  openWechatModal,
}) {
  if (!dropdownName) return null;

  // 在 routes 里找到当前这个顶层菜单
  const current = routes.find((item) => item.name === dropdownName);

  // 如果没配置 collapse（没有子菜单），那就不渲染下拉
  if (!current || !current.collapse) return null;

  const handleItemClick = (item) => (event) => {
    if (item.disabled) {
      event.preventDefault();
      return;
    }

    // 特殊处理：微信客服 -> 打开二维码弹窗
    if (item.name === "微信客服" || item.name === "WeChat") {
      event.preventDefault();
      if (openWechatModal) {
        openWechatModal(event);
      }
      if (onClose) {
        onClose();
      }
      return;
    }

    // 其他普通菜单
    if (item.route) {
      // 处理锚点滚动（如 #pricing）
      if (item.route.startsWith("#")) {
        const target = document.getElementById(item.route.substring(1));
        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      } else {
        // 普通内部跳转
        window.location.href = item.route;
      }
    } else if (item.href) {
      // 外部链接
      if (item.href.startsWith("#")) {
        const target = document.getElementById(item.href.substring(1));
        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      } else {
        window.location.href = item.href;
      }
    }

    if (onClose) {
      onClose();
    }
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(open && anchorEl)}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      keepMounted
    >
      {current.collapse.map((item) => (
        <MenuItem key={item.name} onClick={handleItemClick(item)} disabled={Boolean(item.disabled)}>
          <MKTypography variant="button" color="text" textTransform="none">
            {item.name}
          </MKTypography>
        </MenuItem>
      ))}
    </Menu>
  );
}

NavbarMenu.propTypes = {
  anchorEl: PropTypes.any,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  routes: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      collapse: PropTypes.array,
    })
  ),
  dropdownName: PropTypes.string,
  openWechatModal: PropTypes.func,
};
